import { useQueries, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { axios, APIBASE_REVIVERDOCUMENTARCHIVE, APIBASE_REVIVERAPPLICATION, APIBASE_REVIVERUSERS } from "./ApiConfig";
import { ArchivedDocumentWithDetails, ArchiveDocument, Transaction } from "@/types";

export const getTransactionDocIds = async (transactionId: number, companyId: number) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/transaction/${transactionId}/documents/${companyId}`);

export const useGetTransactionDocIdsSingle = (transactionId: any, companyId: number, options?: any) => {
  return useQuery({
    queryKey: ["getTransactionDocIds", transactionId, companyId],
    queryFn: () => getTransactionDocIds(transactionId, companyId),
    ...options,
  });
};

export const useGetTransactionsDocIds = (transactions: Transaction[], companyId: number, options: any) => {
  return useQueries({
    queries: transactions.map((trans) => ({
      queryKey: ["getTransactionDocIds", trans.id],
      queryFn: async (): Promise<string[]> => {
        const result = await getTransactionDocIds(trans.id, companyId);
        return result.data;
      },
      enabled: !!trans.id,
      refetchOnWindowFocus: false,
      ...options,
    })),
    combine: (results) => {
      return results
        .filter((result) => result.isSuccess && result.data)
        .flatMap((result) => (result.data as any[]).map((doc) => doc.documentId));
    },
  });
};

export const getDocuments = async (companyId: number, id: any) => {
  if (!id) return null;
  return axios.get<ArchivedDocumentWithDetails>(
    `${APIBASE_REVIVERDOCUMENTARCHIVE}api/documents/${companyId}/${id}/details`
  );
};

export const useGetDocumentsFromString = (attachments: string[], companyId: any, enabled?: boolean) => {
  return useQueries({
    queries: attachments
      ? attachments
          .filter((attachment) => !!attachment)
          .map((attachment) => ({
            queryKey: ["getDocumentById", attachment],
            queryFn: () => getDocuments(companyId, attachment),
            // staleTime: 2 * 1000 * 60, //2min
            enabled: enabled ?? true,
          }))
      : [],
    combine: (results) => {
      return {
        images: results
          .filter((result) => (result.data?.data.documentType === "IMAGE" ? result.data?.data : null))
          .map((result) => result.data?.data),
        files: results
          .filter((result) => (result.data?.data.documentType !== "IMAGE" ? result.data?.data : null))
          .map((result) => result.data?.data),
        pending: results.some((result) => result.isPending),
      };
    },
  });
};

export const createDocument = async (documentData: ArchiveDocument) =>
  axios
    .post(`${APIBASE_REVIVERDOCUMENTARCHIVE}api/documents/archive`, documentData)
    .then((res) => res.data)
    .catch((error) => error);

export const createDocumentForCompany = async (documentData: ArchiveDocument) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/settings/documentArchive/upload`, documentData);

export const deleteDocument = async (documentId: string, companyId: number) =>
  axios.delete(`${APIBASE_REVIVERDOCUMENTARCHIVE}api/documents/delete/${companyId}/${documentId}`);

const getImagesByOrderId = async (orderId: number) =>
  axios.get(`${APIBASE_REVIVERDOCUMENTARCHIVE}api/documents/order/${orderId}/images`);

export const useGetImagesByOrderId = (
  orderId: number | null,
  options?: Partial<UseQueryOptions<ArchivedDocumentWithDetails[]>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<ArchivedDocumentWithDetails[]>({
    queryKey: ["getImagesByOrderId", orderId],
    queryFn: async () => {
      const result = await getImagesByOrderId(orderId!);
      return result.data;
    },
    enabled: !!orderId,
    ...queryOptions,
  });
};

export const getDocumentArchiveByCompanyId = async (companyId: number, showDeactivated: boolean) =>
  axios.get(
    `${APIBASE_REVIVERUSERS}api/company/settings/documentArchive/${companyId}?includeDeleted=${showDeactivated}`
  );

export const useGetDocumentArchiveByCompanyId = (
  companyId: number,
  showDeactivated: boolean,
  options?: Partial<UseQueryOptions<ArchivedDocumentWithDetails[]>>
) => {
  return useQuery<ArchivedDocumentWithDetails[]>({
    queryKey: ["getDocumentArchiveByCompanyId", companyId, showDeactivated],
    queryFn: async () => {
      const res = await getDocumentArchiveByCompanyId(companyId, showDeactivated);
      return res.data?.documents;
    },
    ...options,
  });
};

export const getDocumentDetailsById = async (companyId: number, documentId: string) =>
  axios.get<ArchivedDocumentWithDetails>(
    `${APIBASE_REVIVERDOCUMENTARCHIVE}api/documents/${companyId}/${documentId}/details`
  );

export const useGetDocumentDetailsById = (
  companyId: number,
  documentId: string,
  options?: Partial<UseQueryOptions<ArchivedDocumentWithDetails>>
) => {
  return useQuery({
    queryKey: ["getDocumentById", companyId, documentId],
    queryFn: async () => {
      const res = await getDocumentDetailsById(companyId, documentId);
      return res.data;
    },
    ...options,
  });
};

export const getDocumentDownloadUrl = async (companyId: number, documentId: string) =>
  axios.get(`${APIBASE_REVIVERDOCUMENTARCHIVE}api/documents/${companyId}/${documentId}/downloadUrl`);

export const useGetDocumentDownloadUrl = (
  companyId: number,
  documentId: string,
  options?: Partial<UseQueryOptions<{ url: string }>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery({
    queryKey: ["getDocumentDownloadUrl", companyId, documentId],
    queryFn: async () => {
      const result = await getDocumentDownloadUrl(companyId, documentId);
      return result.data;
    },
    ...queryOptions,
  });
};
